import { ref, computed, watch, nextTick, type ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'
import type { ContentLanguage } from '@/domain/entities/content-language.entity'
import { useAccountStore } from '@/stores/account'

export interface UseCourseCategoryOptions {
  /** Функция для загрузки курсов */
  fetchCourses: (params: any) => Promise<any>
  /** Функция для загрузки доступных локалей */
  fetchAllAvailableLocales: (params: { category: string, account_id?: string }) => Promise<void>
  /** Функция для загрузки дополнительных курсов */
  loadMore?: (limit: number) => Promise<void>
  /** Доступные локали (ref) */
  availableLocales: any
  /** Категория курсов (computed) */
  category: ComputedRef<string>
}

/**
 * Composable для логики страницы категории курсов с фильтром по языку
 */
export function useCourseLangSelector(options: UseCourseCategoryOptions) {
  const { fetchCourses, fetchAllAvailableLocales, loadMore, availableLocales, category } = options
  const { locale: interfaceLocale } = useI18n()
  // Школа сайту (by/pl/am/ua) — щоб каталог показував лише підписані нею курси.
  const { account } = storeToRefs(useAccountStore())
  const selectedLocaleFilter = ref<string | null>(null)
  const isFilterInitialized = ref(false) // Флаг, что фильтр уже инициализирован
  /** true после завершения loadCategoryData(); для UI и чтобы watch не вызывал loadCourses при программной смене фильтра внутри loadCategoryData. */
  const isInitialLoadDone = ref(false)
  /** Только для guard в watch: не вызывать loadCourses, пока мы внутри loadCategoryData. */
  let isInsideLoadCategoryData = false
  /** Микро-задержка перед применением фильтра, чтобы уменьшить мигание списка курсов. */
  const FILTER_DEBOUNCE_MS = 80
  let filterDebounceTimer: ReturnType<typeof setTimeout> | null = null

  async function loadCourses() {
    try {
      const params: any = {
        limit: 12,
        course_category: category.value,
        sort_by: selectedLocaleFilter.value ? 'updated_at' : 'course_locale,updated_at',
        order: selectedLocaleFilter.value ? 'desc' : 'asc',
      }

      if (selectedLocaleFilter.value) {
        params.course_locale = selectedLocaleFilter.value
      }

      // Жорстка привязка каталогу до підписок школи (опційно: якщо акаунт визначено).
      const accountId = account.value?._id
      if (accountId) {
        params.account_id = accountId
      }

      await fetchCourses(params)
    } catch (error) {
      console.error('Failed to load courses:', error)
    }
  }

  async function loadMoreCourses() {
    if (loadMore) {
      try {
        await loadMore(12)
      } catch (error) {
        console.error('Failed to load more courses:', error)
      }
    }
  }

  /**
   * Проверяет, есть ли курсы на указанной локали
   */
  const hasCoursesForLocale = (localeCode: string): boolean => {
    const locales = availableLocales.value || []
    if (locales.length === 0) return false
    
    return locales.some((item: string | ContentLanguage) => {
      const code = typeof item === 'string' ? item : item.id
      if (code !== localeCode) return false
      
      return typeof item === 'string' || (item.courseCount ?? 0) > 0
    })
  }

  /**
   * Инициализация фильтра: язык интерфейса (если есть курсы) или "Все"
   */
  function initializeLocaleFilter() {
    if (isFilterInitialized.value || selectedLocaleFilter.value !== null) {
      isFilterInitialized.value = true
      return
    }
    
    const locales = availableLocales.value || []
    if (locales.length === 0) {
      selectedLocaleFilter.value = null
      isFilterInitialized.value = true
      return
    }

    const locale = interfaceLocale.value
    selectedLocaleFilter.value = hasCoursesForLocale(locale) ? locale : null
    isFilterInitialized.value = true
  }

  async function loadCategoryData() {
    isInsideLoadCategoryData = true
    isInitialLoadDone.value = false
    isFilterInitialized.value = false
    selectedLocaleFilter.value = null

    try {
      await fetchAllAvailableLocales({ category: category.value, account_id: account.value?._id })
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 100))

      initializeLocaleFilter()
      await loadCourses()
      // Небольшая задержка перед «готово», чтобы список успел отрисоваться без мигания
      await new Promise(resolve => setTimeout(resolve, FILTER_DEBOUNCE_MS))
    } finally {
      isInsideLoadCategoryData = false
      isInitialLoadDone.value = true
    }
  }

  watch(selectedLocaleFilter, () => {
    if (isInsideLoadCategoryData) return
    if (filterDebounceTimer) clearTimeout(filterDebounceTimer)
    filterDebounceTimer = setTimeout(() => {
      filterDebounceTimer = null
      loadCourses()
    }, FILTER_DEBOUNCE_MS)
  })

  return {
    category,
    selectedLocaleFilter,
    isInitialLoadDone: computed(() => isInitialLoadDone.value),
    loadCourses,
    loadMoreCourses,
    loadCategoryData,
  }
}
